/* eslint-disable react/no-array-index-key */
import { Checkbox, Flex, Text, Link } from '@servicetitan/anvil2';
import {
    LoadingScreen,
    useLegalDisclosure,
    HtmlContent,
    SignatureCanvas,
} from '@servicetitan/financing-utils-shared';
import { useState, useEffect, Fragment } from 'react';
import { ApplicationDataStore } from '../application/ApplicationDataStore';
import './GreenSkyDisclosureText.scss';
import { SetupDataStore } from '@servicetitan/financing-workflow-shared';

interface GreenSkyDisclosureTextProps {
    onDisclosureChange?: (allAgreed: boolean) => void;
    onApplicantSignatureChange?: (base64Signature: string | null) => void;
    onCoApplicantSignatureChange?: (base64Signature: string | null) => void;
}

export const GreenSkyDisclosureText = ({
    onDisclosureChange,
    onApplicantSignatureChange,
    onCoApplicantSignatureChange,
}: GreenSkyDisclosureTextProps) => {
    const { applicationData } = ApplicationDataStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const categories = ['submission', 'submitButton'];

    const [applicantSignature, setApplicantSignature] = useState<string | null>(null);
    const [coApplicantSignature, setCoApplicantSignature] = useState<string | null>(null);

    if (applicationData?.coApplicant?.hasCoApplicant) {
        categories.push('submissionWithCoApplicant', 'submitButtonWithCoApplicant');
    }

    const disclosures = useLegalDisclosure({
        providerName: 'GreenSky',
        tenantId: setupData?.tenantId,
        category: categories,
    }) as string[];

    const [checkboxes, setCheckboxes] = useState<{ [id: string]: boolean }>(() => {
        const initialCheckboxes: { [id: string]: boolean } = {};
        disclosures.forEach((_, index) => {
            initialCheckboxes[index] = false;
        });
        return initialCheckboxes;
    });

    const handleChange = (id: string) => {
        setCheckboxes({ ...checkboxes, [id]: !checkboxes[id] });
    };

    const handleApplicantSignatureChange = (base64Signature: string | null) => {
        setApplicantSignature(base64Signature);
        onApplicantSignatureChange?.(base64Signature);
    };

    const handleCoApplicantSignatureChange = (base64Signature: string | null) => {
        setCoApplicantSignature(base64Signature);
        onCoApplicantSignatureChange?.(base64Signature);
    };

    useEffect(() => {
        const greenSkyAttested = Object.entries(checkboxes).every(([, value]) => value === true);

        const applicantSigned = !!applicantSignature;
        const coApplicantPresent = !!applicationData?.coApplicant?.hasCoApplicant;
        const coApplicantSigned = coApplicantPresent ? !!coApplicantSignature : true;

        const allAttested = greenSkyAttested && applicantSigned && coApplicantSigned;

        onDisclosureChange?.(allAttested);
    }, [checkboxes, applicantSignature, coApplicantSignature, onDisclosureChange, applicationData]);

    if (!disclosures?.length) {
        return <LoadingScreen />;
    }

    return (
        <Flex direction="column" gap="4">
            <Text variant="body">
                Please read the following important agreements and disclosures. This is a credit
                application, and submitting it means that you have read and agree to the steps
                described below. These agreements and disclosures can be found online anytime at{' '}
                <Link target="_blank" rel="noreferrer" href="https://www.greensky.com/disclosures">
                    www.greensky.com/disclosures
                </Link>
                . Please retain a copy of them for your records.
            </Text>
            <Text variant="body">
                Definitions: For the purposes of these disclosures, the following definitions apply:
            </Text>
            <Text variant="body">
                “Lender,” “us,” “we” or “our” shall include any financial institution, and their
                agents, service providers and representatives, participating in the Greensky Loan
                Program (the Greensky Program).
            </Text>
            <Text variant="body">
                1. APPLICATION AGREEMENTS AND DISCLOSURES FOR GREENSKY CONSUMER LOAN PROGRAM
            </Text>
            <Text variant="body">
                We are providing the following disclosures to you in connection with your
                application for a loan from one of the financial institutions offering GreenSky®
                Program.
            </Text>
            {disclosures.map((disclosure, index) => (
                <Fragment key={index}>
                    <Checkbox
                        label={<HtmlContent content={disclosure} />}
                        onChange={() => handleChange(`${index}`)}
                        checked={checkboxes[`${index}`]}
                    />
                    {index === 1 && (
                        <SignatureCanvas
                            onSignatureChange={handleApplicantSignatureChange}
                            label="Sign here"
                            clearButtonText="Clear"
                        />
                    )}
                </Fragment>
            ))}
            {applicationData?.coApplicant?.hasCoApplicant && (
                <SignatureCanvas
                    onSignatureChange={handleCoApplicantSignatureChange}
                    label="Sign here"
                    clearButtonText="Clear"
                />
            )}
        </Flex>
    );
};
